import { sampleSizeBand } from '../shared/sample-size.ts';
import {
  AUTO_ANALYZER_MAX_CELLS,
  AUTO_ANALYZER_MAX_GAMES,
  AUTO_ANALYZER_MAX_MARTINGALE_GAMES,
} from '../shared/constants.ts';
import { theoreticalFixedFullStopExposure } from './fixed-grid.ts';
import {
  profitExposureRatio,
  theoreticalFullStopExposure,
} from './martingale-grid.ts';
import { analyzeDroughts, chronologicalDroughts } from './drought.ts';
import { fixedSpec, martingaleSpec } from './portfolio-simulator.ts';
import { mean, median, maximumDrawdown, quantile } from './stats.ts';
import { StrategyRuntime, type StrategySpec } from './strategy-runtime.ts';
import type { StrategyEpisode } from './simulator.ts';
import type { IndexedRound } from './types.ts';

export const AUTO_ANALYZER_BASE_STAKE = 1;
export const AUTO_ANALYZER_MIN_PROFIT_EXPOSURE = 2;
export const AUTO_ANALYZER_MIN_COMPLETED = 10;
export const AUTO_ANALYZER_MIN_TRIGGERED = 10;
export const AUTO_ANALYZER_MIN_D_EVIDENCE = 5;
export const AUTO_ANALYZER_MIN_REGION_SIZE = 3;
export {
  AUTO_ANALYZER_MAX_GAMES,
  AUTO_ANALYZER_MAX_MARTINGALE_GAMES,
  AUTO_ANALYZER_MAX_CELLS,
};
export const AUTO_ANALYZER_QUICK_TARGETS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 30, 40, 50, 100] as const;
const AUTO_ANALYZER_SIM_CHUNK = 250;

export type AnalyzerStakingMode = 'FIXED' | 'MARTINGALE';
export type RegionStatus = 'STABLE' | 'WEAKENING' | 'BROKEN';
export type StopoutPressure = 'IMPROVING' | 'STABLE' | 'WEAKENING' | 'SEVERELY_WEAKENING';
export type AnalyzerVerdict = 'STRONG_REGION' | 'INSUFFICIENT_HISTORICAL_EVIDENCE';

export interface AnalyzerSearchRange {
  entryFrom: number;
  entryTo: number;
  supportedEntries: number[];
  longestCompletedDrought: number;
  droughtMean: number | null;
  droughtMedian: number | null;
  droughtP90: number | null;
  droughtP99: number | null;
  droughtMax: number | null;
  evidenceThreshold: number;
  truncatedToCellLimit: boolean;
}

export interface StopoutCycle {
  afterStopoutTriggerSequence: number;
  nextStopoutTriggerSequence: number;
  successfulEpisodes: number;
  successfulProfit: number;
  fullStopoutLoss: number;
  coverage: number;
}

export interface CandidateMetrics {
  entryDrought: number;
  wagerCount: number;
  stopDrought: number;
  stakingMode: AnalyzerStakingMode;
  triggeredEpisodes: number;
  completedEpisodes: number;
  successfulEpisodes: number;
  stopOuts: number;
  openEpisodes: number;
  censoredEpisodes: number;
  netPnl: number;
  totalStaked: number;
  roi: number | null;
  profitExposureRatio: number | null;
  fullStopoutLoss: number;
  maximumDrawdown: number;
  cycles: StopoutCycle[];
  medianCycleCoverage: number | null;
  lowerQuartileCycleCoverage: number | null;
  coverageAtLeast1Rate: number | null;
  coverageAtLeast2Rate: number | null;
  coverageAtLeast3Rate: number | null;
  twoXCoverageBeforeNextStopoutRate: number | null;
  lifetimeSuccessCoverage: number | null;
  stopoutPressure: StopoutPressure;
  longestCompletedDrought: number;
  terminalStopOutObserved: boolean;
  sampleSizeBand: ReturnType<typeof sampleSizeBand>;
  winningAttemptCounts: Record<string, number>;
  lateHitShare: number | null;
  medianWinningAttempt: number | null;
  episodes: StrategyEpisode[];
}

export interface EvaluatedCandidate extends CandidateMetrics {
  stage1: boolean;
  stage2: boolean;
  stage3: boolean;
  recentPnl: number | null;
  recentProfitExposure: number | null;
  recentCompleted: number;
  earlyPnl: number | null;
  earlyProfitExposure: number | null;
  regionStatus: RegionStatus;
  walkForwardFoldsSupported: number;
  walkForwardFoldsTotal: number;
}

export interface CandidateRegion {
  members: EvaluatedCandidate[];
  entryFrom: number;
  entryTo: number;
  gamesFrom: number;
  gamesTo: number;
  status: RegionStatus;
  statusReason: string;
  walkForwardFoldsSupported: number;
  walkForwardFoldsTotal: number;
}

export interface SuggestedCell {
  entryDrought: number;
  stopDrought: number;
  wagerCount: number;
  stakingMode: AnalyzerStakingMode;
}

export interface RegionReport {
  entryFrom: number;
  entryTo: number;
  gamesFrom: number;
  gamesTo: number;
  suggested: SuggestedCell;
  status: RegionStatus;
  statusReason: string;
  reasons: string[];
}

export interface AnalyzerHeatmapCell {
  entryDrought: number;
  wagerCount: number;
  stopDrought: number;
  netPnl: number;
  profitExposureRatio: number | null;
  qualified: boolean;
  inPrimaryRegion: boolean;
  isSuggested: boolean;
}

export interface WalkForwardFoldReport {
  fold: number;
  trainStartSequence: number;
  trainEndSequence: number;
  testStartSequence: number;
  testEndSequence: number;
  trainRoundCount: number;
  testRoundCount: number;
  leakedFutureIntoTrain: false;
  supported: boolean;
  testPnl: number | null;
  testProfitExposure: number | null;
}

export interface AutoAnalyzerReport {
  claimKind: 'simulated_historical_profitability';
  sampleLabel: 'IN-SAMPLE HISTORICAL SIMULATION';
  advisoryOnly: true;
  appliesToPaper: false;
  mutatesCollector: false;
  mutatesPaperConfig: false;
  verdict: AnalyzerVerdict;
  insufficientReason: string | null;
  targetMultiplier: number;
  maxGames: number;
  stakingMode: AnalyzerStakingMode;
  baseStake: typeof AUTO_ANALYZER_BASE_STAKE;
  minProfitExposure: typeof AUTO_ANALYZER_MIN_PROFIT_EXPOSURE;
  droughtSearch: AnalyzerSearchRange;
  consideredWagerCounts: number[];
  candidateCount: number;
  primary: RegionReport | null;
  secondary: RegionReport[];
  heatmap: {
    entryFrom: number;
    entryTo: number;
    wagerFrom: number;
    wagerTo: number;
    cells: AnalyzerHeatmapCell[];
  };
  details: {
    suggested: CandidateMetrics | null;
    regionSize: number | null;
    walkForward: WalkForwardFoldReport[];
    droughtSearch: AnalyzerSearchRange;
    consideredEntries: number[];
    consideredWagerCounts: number[];
    qualifiedCount: number;
    regionCount: number;
    evaluatedMaxWager: number;
    searchCells: number;
  };
}

export function analyzerStakingMode(targetMultiplier: number): AnalyzerStakingMode {
  if (!Number.isFinite(targetMultiplier) || targetMultiplier < 2) {
    throw new Error('Analyzer target must be a finite number >= 2.');
  }
  if (targetMultiplier === 2) {
    return 'MARTINGALE';
  }
  return 'FIXED';
}

/** UI suggestion only. Backend always uses the submitted Max Games value. */
export function defaultSuggestedMaxGames(targetMultiplier: number): number {
  if (targetMultiplier === 2) {
    return 6;
  }
  if (Number.isInteger(targetMultiplier) && targetMultiplier > 2) {
    return Math.min(targetMultiplier, AUTO_ANALYZER_MAX_GAMES);
  }
  return 10;
}

export function analyzerFullStopExposure(
  stakingMode: AnalyzerStakingMode,
  wagerCount: number,
  baseStake = AUTO_ANALYZER_BASE_STAKE,
): number {
  if (stakingMode === 'MARTINGALE') {
    return theoreticalFullStopExposure(wagerCount, baseStake);
  }
  return theoreticalFixedFullStopExposure(wagerCount, baseStake);
}

export function analyzerFullStopLoss(
  stakingMode: AnalyzerStakingMode,
  wagerCount: number,
  baseStake = AUTO_ANALYZER_BASE_STAKE,
): number {
  return analyzerFullStopExposure(stakingMode, wagerCount, baseStake);
}

export function enumerateAnalyzerCandidates(
  entries: readonly number[],
  maxGames: number,
): Array<{ entryDrought: number; wagerCount: number; stopDrought: number }> {
  if (!Number.isInteger(maxGames) || maxGames < 1) {
    throw new Error('Max games must be an integer >= 1.');
  }
  const cells: Array<{ entryDrought: number; wagerCount: number; stopDrought: number }> = [];
  for (const entryDrought of entries) {
    for (let wagerCount = 1; wagerCount <= maxGames; wagerCount += 1) {
      cells.push({
        entryDrought,
        wagerCount,
        stopDrought: entryDrought + wagerCount,
      });
    }
  }
  return cells;
}

export function experiencedDroughtSearchRange(
  rounds: readonly IndexedRound[],
  targetMultiplier: number,
  maxGames: number,
): AnalyzerSearchRange {
  const analysis = analyzeDroughts(rounds, targetMultiplier);
  const all = chronologicalDroughts(analysis);
  const completedLengths = analysis.completedDroughts.map((drought) => drought.lengthRounds);
  const longestCompleted = analysis.longestCompletedDroughtRounds;
  const evidenceThreshold = AUTO_ANALYZER_MIN_D_EVIDENCE;
  const supported: number[] = [];
  for (let entry = 1; entry <= longestCompleted; entry += 1) {
    const evidence = all.filter((drought) => drought.lengthRounds >= entry).length;
    if (evidence >= evidenceThreshold) {
      supported.push(entry);
    }
  }
  if (supported.length === 0 && longestCompleted >= 1) {
    for (let entry = 1; entry <= longestCompleted; entry += 1) {
      if (all.some((drought) => drought.lengthRounds >= entry)) {
        supported.push(entry);
      }
    }
  }
  const searchCells = supported.length * Math.max(1, maxGames);
  if (searchCells > AUTO_ANALYZER_MAX_CELLS) {
    throw new Error(
      `Analyzer search has ${searchCells.toLocaleString('en-US')} D×W cells (limit ${AUTO_ANALYZER_MAX_CELLS.toLocaleString('en-US')}). Narrow Max Games or wait for a longer drought evidence filter. W was not truncated.`,
    );
  }
  return {
    entryFrom: supported[0] ?? 0,
    entryTo: supported[supported.length - 1] ?? 0,
    supportedEntries: supported,
    longestCompletedDrought: longestCompleted,
    droughtMean: mean(completedLengths),
    droughtMedian: median(completedLengths),
    droughtP90: quantile(completedLengths, 0.9),
    droughtP99: quantile(completedLengths, 0.99),
    droughtMax: completedLengths.length === 0 ? null : Math.max(...completedLengths),
    evidenceThreshold,
    truncatedToCellLimit: false,
  };
}

export function simulateIndependentStrategies(
  rounds: readonly IndexedRound[],
  specs: readonly StrategySpec[],
): StrategyRuntime[] {
  const runtimes = specs.map((spec) => new StrategyRuntime(spec));
  for (let index = 0; index < rounds.length; index += 1) {
    const round = rounds[index];
    if (!round) {
      continue;
    }
    const previous = rounds[index - 1];
    const starts = runtimes.map((runtime) => runtime.beginRound(previous, round));
    for (let specIndex = 0; specIndex < runtimes.length; specIndex += 1) {
      const runtime = runtimes[specIndex];
      const start = starts[specIndex];
      if (!runtime || !start) {
        continue;
      }
      runtime.settle(round, start.eligible ? start.proposedStake : null);
    }
  }
  for (const runtime of runtimes) {
    runtime.closeOpen();
  }
  return runtimes;
}

export function stopoutCyclesFromEpisodes(
  episodes: readonly StrategyEpisode[],
  fullStopoutLoss: number,
): StopoutCycle[] {
  const completed = episodes.filter((episode) => episode.outcome === 'success' || episode.outcome === 'failure');
  const cycles: StopoutCycle[] = [];
  let seenStopout = false;
  let successProfit = 0;
  let successCount = 0;
  let lastStopoutTrigger = 0;
  for (const episode of completed) {
    if (episode.outcome === 'success') {
      successProfit += episode.netPnl;
      successCount += 1;
      continue;
    }
    if (seenStopout) {
      cycles.push({
        afterStopoutTriggerSequence: lastStopoutTrigger,
        nextStopoutTriggerSequence: episode.triggerSequence,
        successfulEpisodes: successCount,
        successfulProfit: successProfit,
        fullStopoutLoss,
        coverage: fullStopoutLoss > 0 ? successProfit / fullStopoutLoss : 0,
      });
    }
    seenStopout = true;
    lastStopoutTrigger = episode.triggerSequence;
    successProfit = 0;
    successCount = 0;
  }
  return cycles;
}

export function twoXCoverageBeforeNextStopoutRate(cycles: readonly StopoutCycle[]): number | null {
  if (cycles.length === 0) {
    return null;
  }
  return cycles.filter((cycle) => cycle.coverage >= 2).length / cycles.length;
}

/** Winning wager number inside the episode window. W means actual wagers placed. */
export function winningWagerNumber(episode: StrategyEpisode): number | null {
  if (episode.outcome !== 'success' || episode.attempts < 1) {
    return null;
  }
  return episode.attempts;
}

export function lateHitShare(episodes: readonly StrategyEpisode[], wagerCount: number): number | null {
  const wins = episodes
    .map((episode) => winningWagerNumber(episode))
    .filter((value): value is number => value !== null);
  if (wins.length === 0 || wagerCount < 1) {
    return null;
  }
  const lateFrom = Math.max(2, Math.ceil(wagerCount * 0.6));
  return wins.filter((attempt) => attempt >= lateFrom).length / wins.length;
}

export function winningAttemptCounts(episodes: readonly StrategyEpisode[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const episode of episodes) {
    const wager = winningWagerNumber(episode);
    if (wager === null) {
      continue;
    }
    const key = String(wager);
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}

export interface MarginalWDelta {
  extraGames: number;
  deltaPnl: number;
  deltaStopOuts: number;
  deltaSuccesses: number;
  deltaProfitExposure: number | null;
  deltaDrawdown: number;
  deltaMedianCycleCoverage: number | null;
}

export function marginalWDelta(shorter: CandidateMetrics, longer: CandidateMetrics): MarginalWDelta {
  if (shorter.entryDrought !== longer.entryDrought) {
    throw new Error('Marginal W comparison requires the same entry D.');
  }
  if (longer.wagerCount <= shorter.wagerCount) {
    throw new Error('Marginal W comparison requires a longer window.');
  }
  const shorterPe = shorter.profitExposureRatio;
  const longerPe = longer.profitExposureRatio;
  return {
    extraGames: longer.wagerCount - shorter.wagerCount,
    deltaPnl: longer.netPnl - shorter.netPnl,
    deltaStopOuts: longer.stopOuts - shorter.stopOuts,
    deltaSuccesses: longer.successfulEpisodes - shorter.successfulEpisodes,
    deltaProfitExposure:
      shorterPe === null || longerPe === null || !Number.isFinite(shorterPe) || !Number.isFinite(longerPe)
        ? null
        : longerPe - shorterPe,
    deltaDrawdown: longer.maximumDrawdown - shorter.maximumDrawdown,
    deltaMedianCycleCoverage:
      shorter.medianCycleCoverage === null || longer.medianCycleCoverage === null
        ? null
        : longer.medianCycleCoverage - shorter.medianCycleCoverage,
  };
}

export function classifyStopoutPressure(cycles: readonly StopoutCycle[]): StopoutPressure {
  if (cycles.length < 4) {
    return 'STABLE';
  }
  const mid = Math.floor(cycles.length / 2);
  const first = median(cycles.slice(0, mid).map((cycle) => cycle.coverage)) ?? 0;
  const second = median(cycles.slice(mid).map((cycle) => cycle.coverage)) ?? 0;
  if (first > 0 && second < first * 0.5) {
    return 'SEVERELY_WEAKENING';
  }
  if (first >= 2 && second < 1) {
    return 'SEVERELY_WEAKENING';
  }
  if (second > first * 1.15) {
    return 'IMPROVING';
  }
  if (second < first * 0.85) {
    return 'WEAKENING';
  }
  return 'STABLE';
}

export function classifyRegionStatus(input: {
  fullQualifies: boolean;
  earlyProfitExposure: number | null;
  earlyPnl: number | null;
  recentProfitExposure: number | null;
  recentPnl: number | null;
  recentCompleted: number;
  stopoutPressure: StopoutPressure;
}): { status: RegionStatus; reason: string } {
  const earlyStrong =
    (input.earlyPnl ?? 0) > 0 &&
    input.earlyProfitExposure !== null &&
    input.earlyProfitExposure >= AUTO_ANALYZER_MIN_PROFIT_EXPOSURE;
  const recentNegative = (input.recentPnl ?? 0) < 0 && input.recentCompleted >= 5;
  const recentMeetsFloor =
    (input.recentPnl ?? 0) > 0 &&
    input.recentProfitExposure !== null &&
    input.recentProfitExposure >= AUTO_ANALYZER_MIN_PROFIT_EXPOSURE;
  const recentStillProfitable = (input.recentPnl ?? 0) >= 0 && input.recentCompleted >= 5;

  if (!input.fullQualifies) {
    return {
      status: 'BROKEN',
      reason:
        'The previous profitable concentration no longer satisfies the required profit/exposure and historical stability checks.',
    };
  }
  if (earlyStrong && recentNegative && (input.recentProfitExposure ?? 0) < 0) {
    return {
      status: 'BROKEN',
      reason:
        'The previous profitable concentration no longer satisfies the required profit/exposure and historical stability checks.',
    };
  }
  if (
    earlyStrong &&
    !recentMeetsFloor &&
    (input.stopoutPressure === 'WEAKENING' ||
      input.stopoutPressure === 'SEVERELY_WEAKENING' ||
      (recentStillProfitable &&
        input.recentProfitExposure !== null &&
        input.recentProfitExposure < AUTO_ANALYZER_MIN_PROFIT_EXPOSURE) ||
      ((input.recentPnl ?? 0) >= 0 &&
        (input.earlyProfitExposure ?? 0) - (input.recentProfitExposure ?? 0) >= 0.5))
  ) {
    return {
      status: 'WEAKENING',
      reason: 'The same profitable region is still present, but stopouts have consumed a larger share of profits in recent data.',
    };
  }
  if (recentMeetsFloor || input.recentCompleted < 5) {
    return {
      status: 'STABLE',
      reason: 'The same profitable area persists and economics remain broadly healthy.',
    };
  }
  if ((input.recentPnl ?? 0) > 0) {
    return {
      status: 'WEAKENING',
      reason: 'The same profitable region is still present, but stopouts have consumed a larger share of profits in recent data.',
    };
  }
  return {
    status: 'STABLE',
    reason: 'The same profitable area persists and economics remain broadly healthy.',
  };
}

export function walkForwardFolds(roundCount: number): Array<{ trainEnd: number; testEnd: number }> {
  if (roundCount < 80) {
    return [];
  }
  if (roundCount < 400) {
    return [{ trainEnd: Math.floor(roundCount * 0.6), testEnd: roundCount }];
  }
  return [
    { trainEnd: Math.floor(roundCount * 0.4), testEnd: Math.floor(roundCount * 0.7) },
    { trainEnd: Math.floor(roundCount * 0.7), testEnd: roundCount },
  ];
}

export function chronologicalPeriodSlices(roundCount: number): Array<{ name: 'early' | 'recent'; start: number; end: number }> {
  const third = Math.floor(roundCount / 3);
  return [
    { name: 'early', start: 0, end: Math.max(third, 1) },
    { name: 'recent', start: Math.floor(roundCount * 0.7), end: roundCount },
  ];
}

export function detectCandidateRegions(candidates: readonly EvaluatedCandidate[]): CandidateRegion[] {
  const qualified = candidates.filter((candidate) => candidate.stage1 && candidate.stage2 && candidate.stage3);
  const index = new Map<string, EvaluatedCandidate>();
  for (const candidate of qualified) {
    index.set(cellKey(candidate.entryDrought, candidate.wagerCount), candidate);
  }
  const visited = new Set<string>();
  const regions: CandidateRegion[] = [];
  for (const candidate of qualified) {
    const startKey = cellKey(candidate.entryDrought, candidate.wagerCount);
    if (visited.has(startKey)) {
      continue;
    }
    const stack = [candidate];
    const members: EvaluatedCandidate[] = [];
    visited.add(startKey);
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) {
        continue;
      }
      members.push(current);
      for (const neighbor of neighborsOf(current, index)) {
        const key = cellKey(neighbor.entryDrought, neighbor.wagerCount);
        if (visited.has(key)) {
          continue;
        }
        visited.add(key);
        stack.push(neighbor);
      }
    }
    members.sort(compareCells);
    const statuses = members.map((member) => member.regionStatus);
    const status: RegionStatus = statuses.includes('STABLE')
      ? statuses.every((value) => value === 'STABLE')
        ? 'STABLE'
        : 'WEAKENING'
      : statuses.includes('WEAKENING')
        ? 'WEAKENING'
        : 'BROKEN';
    const statusReason =
      members.find((member) => member.regionStatus === status)?.regionStatus === status
        ? classifyReasonFromStatus(status)
        : classifyReasonFromStatus(status);
    regions.push({
      members,
      entryFrom: Math.min(...members.map((member) => member.entryDrought)),
      entryTo: Math.max(...members.map((member) => member.entryDrought)),
      gamesFrom: Math.min(...members.map((member) => member.wagerCount)),
      gamesTo: Math.max(...members.map((member) => member.wagerCount)),
      status,
      statusReason,
      walkForwardFoldsSupported: majority(members.map((member) => member.walkForwardFoldsSupported)),
      walkForwardFoldsTotal: majority(members.map((member) => member.walkForwardFoldsTotal)),
    });
  }
  return regions.sort(compareRegions);
}

export function selectRepresentativeCell(region: CandidateRegion): EvaluatedCandidate {
  const medianEntry = median(region.members.map((member) => member.entryDrought)) ?? region.members[0]!.entryDrought;
  const medianGames = median(region.members.map((member) => member.wagerCount)) ?? region.members[0]!.wagerCount;
  const edgeEntries = new Set([region.entryFrom, region.entryTo]);
  const edgeGames = new Set([region.gamesFrom, region.gamesTo]);
  return [...region.members].sort((left, right) => {
    const leftDistance = chebyshev(left.entryDrought, left.wagerCount, medianEntry, medianGames);
    const rightDistance = chebyshev(right.entryDrought, right.wagerCount, medianEntry, medianGames);
    if (leftDistance !== rightDistance) {
      return leftDistance - rightDistance;
    }
    const leftInterior = Number(!(edgeEntries.has(left.entryDrought) || edgeGames.has(left.wagerCount)));
    const rightInterior = Number(!(edgeEntries.has(right.entryDrought) || edgeGames.has(right.wagerCount)));
    if (leftInterior !== rightInterior) {
      return rightInterior - leftInterior;
    }
    if (left.walkForwardFoldsSupported !== right.walkForwardFoldsSupported) {
      return right.walkForwardFoldsSupported - left.walkForwardFoldsSupported;
    }
    const leftCoverage = left.twoXCoverageBeforeNextStopoutRate ?? left.medianCycleCoverage ?? 0;
    const rightCoverage = right.twoXCoverageBeforeNextStopoutRate ?? right.medianCycleCoverage ?? 0;
    if (leftCoverage !== rightCoverage) {
      return rightCoverage - leftCoverage;
    }
    if (left.completedEpisodes !== right.completedEpisodes) {
      return right.completedEpisodes - left.completedEpisodes;
    }
    if (left.maximumDrawdown !== right.maximumDrawdown) {
      return left.maximumDrawdown - right.maximumDrawdown;
    }
    if (left.wagerCount !== right.wagerCount) {
      return left.wagerCount - right.wagerCount;
    }
    return left.entryDrought - right.entryDrought;
  })[0]!;
}

export function analyzeAutoStrategy(
  rounds: readonly IndexedRound[],
  options: { targetMultiplier: number; maxGames: number },
): AutoAnalyzerReport {
  const targetMultiplier = options.targetMultiplier;
  const maxGames = options.maxGames;
  if (!Number.isFinite(targetMultiplier) || targetMultiplier < 2) {
    throw new Error('Analyzer target must be a finite number >= 2.');
  }
  if (!Number.isInteger(maxGames) || maxGames < 1 || maxGames > AUTO_ANALYZER_MAX_GAMES) {
    throw new Error(`Max games must be an integer from 1 to ${AUTO_ANALYZER_MAX_GAMES}.`);
  }
  const stakingMode = analyzerStakingMode(targetMultiplier);
  if (stakingMode === 'MARTINGALE' && maxGames > AUTO_ANALYZER_MAX_MARTINGALE_GAMES) {
    throw new Error(
      `Martingale maximum games cannot exceed ${AUTO_ANALYZER_MAX_MARTINGALE_GAMES} actual wagers. This safety ceiling does not replace the Max Games value you submit.`,
    );
  }
  const droughtSearch = experiencedDroughtSearchRange(rounds, targetMultiplier, maxGames);
  const consideredWagerCounts = axis(1, maxGames);
  const empty = emptyReport({
    targetMultiplier,
    maxGames,
    stakingMode,
    droughtSearch,
    consideredWagerCounts,
    insufficientReason: 'Not enough historical drought evidence to search entry values.',
  });
  if (droughtSearch.supportedEntries.length === 0 || rounds.length === 0) {
    return empty;
  }

  const fullMetrics = simulateGrid(rounds, targetMultiplier, stakingMode, droughtSearch.supportedEntries, maxGames);
  const stage2Cells = fullMetrics.filter((metrics) => qualifiesHard(metrics) && qualifiesCycles(metrics));
  const slices = chronologicalPeriodSlices(rounds.length);
  const earlySlice = slices.find((slice) => slice.name === 'early');
  const recentSlice = slices.find((slice) => slice.name === 'recent');
  const earlyMetrics =
    earlySlice && earlySlice.end - earlySlice.start >= 20 && stage2Cells.length > 0
      ? indexMetrics(simulateCells(rounds.slice(earlySlice.start, earlySlice.end), targetMultiplier, stakingMode, stage2Cells))
      : new Map<string, CandidateMetrics>();
  const recentMetrics =
    recentSlice && recentSlice.end - recentSlice.start >= 20 && stage2Cells.length > 0
      ? indexMetrics(simulateCells(rounds.slice(recentSlice.start, recentSlice.end), targetMultiplier, stakingMode, stage2Cells))
      : new Map<string, CandidateMetrics>();

  const evaluated: EvaluatedCandidate[] = fullMetrics.map((metrics) => {
    const early = earlyMetrics.get(cellKey(metrics.entryDrought, metrics.wagerCount));
    const recent = recentMetrics.get(cellKey(metrics.entryDrought, metrics.wagerCount));
    const stage1 = qualifiesHard(metrics);
    const stage2 = stage1 && qualifiesCycles(metrics);
    const classified = classifyRegionStatus({
      fullQualifies: stage1 && stage2,
      earlyProfitExposure: early?.profitExposureRatio ?? null,
      earlyPnl: early?.netPnl ?? null,
      recentProfitExposure: recent?.profitExposureRatio ?? null,
      recentPnl: recent?.netPnl ?? null,
      recentCompleted: recent?.completedEpisodes ?? 0,
      stopoutPressure: metrics.stopoutPressure,
    });
    const stage3 = stage2 && classified.status !== 'BROKEN';
    return {
      ...metrics,
      stage1,
      stage2,
      stage3,
      recentPnl: recent?.netPnl ?? null,
      recentProfitExposure: recent?.profitExposureRatio ?? null,
      recentCompleted: recent?.completedEpisodes ?? 0,
      earlyPnl: early?.netPnl ?? null,
      earlyProfitExposure: early?.profitExposureRatio ?? null,
      regionStatus: classified.status,
      walkForwardFoldsSupported: 0,
      walkForwardFoldsTotal: 0,
    };
  });

  const folds = walkForwardFolds(rounds.length);
  const walkForward: WalkForwardFoldReport[] = [];
  for (const [foldIndex, fold] of folds.entries()) {
    const train = rounds.slice(0, fold.trainEnd);
    const test = rounds.slice(fold.trainEnd, fold.testEnd);
    const trainFirst = train[0];
    const trainLast = train[train.length - 1];
    const testFirst = test[0];
    const testLast = test[test.length - 1];
    if (!trainFirst || !trainLast || !testFirst || !testLast) {
      continue;
    }
    if (trainLast.sequenceNumber >= testFirst.sequenceNumber) {
      throw new Error('Walk-forward train window leaked into the test window.');
    }
    const trainRange = experiencedDroughtSearchRange(train, targetMultiplier, maxGames);
    const trainEvaluated = simulateGrid(train, targetMultiplier, stakingMode, trainRange.supportedEntries, maxGames).map(
      (metrics) => toPreWalkCandidate(metrics),
    );
    const trainRegions = detectCandidateRegions(trainEvaluated).filter((region) => region.members.length >= AUTO_ANALYZER_MIN_REGION_SIZE);
    const selectedKeys = new Set<string>();
    for (const region of trainRegions) {
      const representative = selectRepresentativeCell(region);
      selectedKeys.add(cellKey(representative.entryDrought, representative.wagerCount));
      for (const member of region.members) {
        selectedKeys.add(cellKey(member.entryDrought, member.wagerCount));
      }
    }
    const testMetrics = indexMetrics(
      simulateCells(
        test,
        targetMultiplier,
        stakingMode,
        trainRegions.flatMap((region) => region.members),
      ),
    );
    let supported = false;
    let testPnl: number | null = null;
    let testProfitExposure: number | null = null;
    if (trainRegions[0]) {
      const representative = selectRepresentativeCell(trainRegions[0]);
      const tested = testMetrics.get(cellKey(representative.entryDrought, representative.wagerCount));
      testPnl = tested?.netPnl ?? null;
      testProfitExposure = tested?.profitExposureRatio ?? null;
      supported = (tested?.netPnl ?? 0) > 0 && (tested?.profitExposureRatio ?? 0) >= 1;
    }
    walkForward.push({
      fold: foldIndex + 1,
      trainStartSequence: trainFirst.sequenceNumber,
      trainEndSequence: trainLast.sequenceNumber,
      testStartSequence: testFirst.sequenceNumber,
      testEndSequence: testLast.sequenceNumber,
      trainRoundCount: train.length,
      testRoundCount: test.length,
      leakedFutureIntoTrain: false,
      supported,
      testPnl,
      testProfitExposure,
    });
    for (const candidate of evaluated) {
      candidate.walkForwardFoldsTotal += 1;
      if (selectedKeys.has(cellKey(candidate.entryDrought, candidate.wagerCount)) && supported) {
        candidate.walkForwardFoldsSupported += 1;
      }
    }
  }

  const regions = detectCandidateRegions(evaluated);
  const broad = regions.filter((region) => region.members.length >= AUTO_ANALYZER_MIN_REGION_SIZE);
  const usable = (broad.length > 0 ? broad : []).filter((region) => region.status !== 'BROKEN' || broad.every((item) => item.status === 'BROKEN'));
  const ranked = [...usable].sort(compareRegions);
  const surviving = ranked.filter((region) => region.status === 'STABLE' || region.status === 'WEAKENING');
  const chosenPool = surviving.length > 0 ? surviving : ranked;

  if (chosenPool.length === 0) {
    const qualifiedCount = evaluated.filter((candidate) => candidate.stage1).length;
    return {
      ...empty,
      candidateCount: evaluated.length,
      heatmap: heatmapFrom(evaluated, null, null),
      details: {
        ...empty.details,
        qualifiedCount,
        consideredEntries: droughtSearch.supportedEntries,
        evaluatedMaxWager: maxGames,
        searchCells: evaluated.length,
      },
      insufficientReason:
        qualifiedCount === 0
          ? 'INSUFFICIENT HISTORICAL EVIDENCE. No candidate met the profit, exposure, and sample-size requirements.'
          : 'INSUFFICIENT HISTORICAL EVIDENCE. Profitable cells existed, but none formed a broad stable region.',
    };
  }

  const primaryRegion = chosenPool[0]!;
  const secondaryRegions = chosenPool.slice(1, 3).filter((region) => region.status !== 'BROKEN');
  const primaryReport = toRegionReport(primaryRegion, maxGames, walkForward, evaluated);
  const secondaryReports = secondaryRegions.map((region) => toRegionReport(region, maxGames, walkForward, evaluated));
  const suggestedMetrics = selectRepresentativeCell(primaryRegion);
  const verdict: AnalyzerVerdict =
    primaryReport.status === 'BROKEN' ? 'INSUFFICIENT_HISTORICAL_EVIDENCE' : 'STRONG_REGION';

  return {
    claimKind: 'simulated_historical_profitability',
    sampleLabel: 'IN-SAMPLE HISTORICAL SIMULATION',
    advisoryOnly: true,
    appliesToPaper: false,
    mutatesCollector: false,
    mutatesPaperConfig: false,
    verdict,
    insufficientReason:
      verdict === 'INSUFFICIENT_HISTORICAL_EVIDENCE'
        ? primaryReport.statusReason
        : null,
    targetMultiplier,
    maxGames,
    stakingMode,
    baseStake: AUTO_ANALYZER_BASE_STAKE,
    minProfitExposure: AUTO_ANALYZER_MIN_PROFIT_EXPOSURE,
    droughtSearch,
    consideredWagerCounts,
    candidateCount: evaluated.length,
    primary: verdict === 'STRONG_REGION' || primaryReport.status === 'BROKEN' ? primaryReport : null,
    secondary: secondaryReports,
    heatmap: heatmapFrom(evaluated, primaryRegion, suggestedMetrics),
    details: {
      suggested: stripEpisodes(suggestedMetrics),
      regionSize: primaryRegion.members.length,
      walkForward,
      droughtSearch,
      consideredEntries: droughtSearch.supportedEntries,
      consideredWagerCounts,
      qualifiedCount: evaluated.filter((candidate) => candidate.stage1 && candidate.stage2).length,
      regionCount: broad.length,
      evaluatedMaxWager: maxGames,
      searchCells: evaluated.length,
    },
  };
}

function simulateGrid(
  rounds: readonly IndexedRound[],
  targetMultiplier: number,
  stakingMode: AnalyzerStakingMode,
  entries: readonly number[],
  maxGames: number,
): CandidateMetrics[] {
  return simulateCells(rounds, targetMultiplier, stakingMode, enumerateAnalyzerCandidates(entries, maxGames));
}

function simulateCells(
  rounds: readonly IndexedRound[],
  targetMultiplier: number,
  stakingMode: AnalyzerStakingMode,
  cells: readonly { entryDrought: number; wagerCount: number; stopDrought: number }[],
): CandidateMetrics[] {
  if (cells.length === 0 || rounds.length === 0) {
    return [];
  }
  const longestCompleted = analyzeDroughts(rounds, targetMultiplier).longestCompletedDroughtRounds;
  const results: CandidateMetrics[] = [];
  for (let offset = 0; offset < cells.length; offset += AUTO_ANALYZER_SIM_CHUNK) {
    const chunk = cells.slice(offset, offset + AUTO_ANALYZER_SIM_CHUNK);
    const specs = chunk.map((cell) =>
      stakingMode === 'MARTINGALE'
        ? martingaleSpec(cellKey(cell.entryDrought, cell.wagerCount), cell.entryDrought, cell.stopDrought, AUTO_ANALYZER_BASE_STAKE)
        : fixedSpec(
            cellKey(cell.entryDrought, cell.wagerCount),
            targetMultiplier,
            cell.entryDrought,
            cell.stopDrought,
            AUTO_ANALYZER_BASE_STAKE,
          ),
    );
    const runtimes = simulateIndependentStrategies(rounds, specs);
    for (let index = 0; index < runtimes.length; index += 1) {
      results.push(metricsFromRuntime(runtimes[index]!, chunk[index]!, longestCompleted));
    }
  }
  return results;
}

function metricsFromRuntime(
  runtime: StrategyRuntime,
  cell: { entryDrought: number; wagerCount: number; stopDrought: number },
  longestCompletedDrought: number,
): CandidateMetrics {
  const episodes = runtime.episodes;
  const completed = episodes.filter((episode) => episode.outcome === 'success' || episode.outcome === 'failure');
  const successful = completed.filter((episode) => episode.outcome === 'success');
  const failed = completed.filter((episode) => episode.outcome === 'failure');
  const totalStaked = completed.reduce((sum, episode) => sum + episode.totalStaked, 0);
  const netPnl = completed.reduce((sum, episode) => sum + episode.netPnl, 0);
  const curve = [0];
  let cumulative = 0;
  for (const episode of completed) {
    cumulative += episode.netPnl;
    curve.push(cumulative);
  }
  const stakingMode = runtime.spec.stakingMode === 'MARTINGALE' ? 'MARTINGALE' : 'FIXED';
  const fullStopoutLoss = analyzerFullStopLoss(stakingMode, cell.wagerCount, runtime.spec.baseStake);
  const cycles = stopoutCyclesFromEpisodes(episodes, fullStopoutLoss);
  const coverages = cycles.map((cycle) => cycle.coverage);
  const successProfit = successful.reduce((sum, episode) => sum + episode.netPnl, 0);
  return {
    entryDrought: cell.entryDrought,
    wagerCount: cell.wagerCount,
    stopDrought: cell.stopDrought,
    stakingMode,
    triggeredEpisodes: episodes.length,
    completedEpisodes: completed.length,
    successfulEpisodes: successful.length,
    stopOuts: failed.length,
    openEpisodes: episodes.filter((episode) => episode.outcome === 'open').length,
    censoredEpisodes: episodes.filter((episode) => episode.outcome === 'censored_gap').length,
    netPnl,
    totalStaked,
    roi: totalStaked === 0 ? null : netPnl / totalStaked,
    profitExposureRatio: profitExposureRatio(netPnl, fullStopoutLoss),
    fullStopoutLoss,
    maximumDrawdown: maximumDrawdown(curve),
    cycles,
    medianCycleCoverage: median(coverages),
    lowerQuartileCycleCoverage: quantile(coverages, 0.25),
    coverageAtLeast1Rate: rateAtLeast(coverages, 1),
    coverageAtLeast2Rate: rateAtLeast(coverages, 2),
    coverageAtLeast3Rate: rateAtLeast(coverages, 3),
    twoXCoverageBeforeNextStopoutRate: twoXCoverageBeforeNextStopoutRate(cycles),
    lifetimeSuccessCoverage: fullStopoutLoss > 0 ? successProfit / fullStopoutLoss : null,
    stopoutPressure: classifyStopoutPressure(cycles),
    longestCompletedDrought,
    terminalStopOutObserved: failed.length > 0,
    sampleSizeBand: sampleSizeBand(completed.length),
    winningAttemptCounts: winningAttemptCounts(episodes),
    lateHitShare: lateHitShare(episodes, cell.wagerCount),
    medianWinningAttempt: median(
      successful.map((episode) => winningWagerNumber(episode)).filter((value): value is number => value !== null),
    ),
    episodes: [],
  };
}

function qualifiesHard(metrics: CandidateMetrics): boolean {
  return (
    metrics.triggeredEpisodes >= AUTO_ANALYZER_MIN_TRIGGERED &&
    metrics.completedEpisodes >= AUTO_ANALYZER_MIN_COMPLETED &&
    metrics.netPnl > 0 &&
    metrics.profitExposureRatio !== null &&
    metrics.profitExposureRatio >= AUTO_ANALYZER_MIN_PROFIT_EXPOSURE
  );
}

function qualifiesCycles(metrics: CandidateMetrics): boolean {
  if (metrics.stopOuts < 3) {
    return true;
  }
  const medianCoverage = metrics.medianCycleCoverage ?? 0;
  const twoX = metrics.twoXCoverageBeforeNextStopoutRate ?? 0;
  return medianCoverage >= 1 || twoX >= 0.25;
}

function toPreWalkCandidate(metrics: CandidateMetrics): EvaluatedCandidate {
  return {
    ...metrics,
    stage1: qualifiesHard(metrics),
    stage2: qualifiesHard(metrics) && qualifiesCycles(metrics),
    stage3: qualifiesHard(metrics) && qualifiesCycles(metrics),
    recentPnl: metrics.netPnl,
    recentProfitExposure: metrics.profitExposureRatio,
    recentCompleted: metrics.completedEpisodes,
    earlyPnl: metrics.netPnl,
    earlyProfitExposure: metrics.profitExposureRatio,
    regionStatus: 'STABLE',
    walkForwardFoldsSupported: 0,
    walkForwardFoldsTotal: 0,
  };
}

function toRegionReport(
  region: CandidateRegion,
  maxGames: number,
  folds: readonly WalkForwardFoldReport[],
  evaluated: readonly EvaluatedCandidate[],
): RegionReport {
  const suggested = selectRepresentativeCell(region);
  const reasons = regionReasons(region, suggested, maxGames, folds, evaluated);
  const classified = {
    status: region.status,
    reason: region.statusReason,
  };
  return {
    entryFrom: region.entryFrom,
    entryTo: region.entryTo,
    gamesFrom: region.gamesFrom,
    gamesTo: region.gamesTo,
    suggested: {
      entryDrought: suggested.entryDrought,
      stopDrought: suggested.stopDrought,
      wagerCount: suggested.wagerCount,
      stakingMode: suggested.stakingMode,
    },
    status: classified.status,
    statusReason: classified.reason,
    reasons,
  };
}

function regionReasons(
  region: CandidateRegion,
  suggested: EvaluatedCandidate,
  maxGames: number,
  folds: readonly WalkForwardFoldReport[],
  evaluated: readonly EvaluatedCandidate[],
): string[] {
  const reasons: string[] = [];
  if ((suggested.profitExposureRatio ?? 0) >= AUTO_ANALYZER_MIN_PROFIT_EXPOSURE && suggested.netPnl > 0) {
    reasons.push('Profit historically exceeded the required 2x exposure level');
  }
  const twoX = suggested.twoXCoverageBeforeNextStopoutRate;
  const medianCoverage = suggested.medianCycleCoverage;
  if ((twoX !== null && twoX >= 0.4) || (medianCoverage !== null && medianCoverage >= 1)) {
    reasons.push('Successful episodes generally rebuilt losses between major stopouts');
  }
  const lateShare = suggested.lateHitShare;
  const laterWagersUsed =
    suggested.stakingMode === 'FIXED' &&
    suggested.wagerCount >= 8 &&
    ((lateShare !== null && lateShare >= 0.2) ||
      (suggested.medianWinningAttempt !== null && suggested.medianWinningAttempt >= Math.ceil(suggested.wagerCount * 0.4)));
  if (laterWagersUsed) {
    reasons.push('Later wagers materially contributed to successful recoveries');
  }
  if (region.members.length >= AUTO_ANALYZER_MIN_REGION_SIZE) {
    reasons.push('Nearby entry/duration combinations also remained profitable');
  }
  if (suggested.regionStatus === 'STABLE' || region.status === 'STABLE') {
    reasons.push('The region persisted through different periods of the dataset');
  }
  if (folds.length > 0 && folds.some((fold) => fold.supported)) {
    reasons.push('Historical forward tests continued to support the region');
  }
  if ((suggested.recentProfitExposure ?? 0) >= AUTO_ANALYZER_MIN_PROFIT_EXPOSURE && (suggested.recentPnl ?? 0) > 0) {
    reasons.push('Recent data still supports the region');
  }
  const longer = evaluated.find(
    (candidate) =>
      candidate.entryDrought === suggested.entryDrought && candidate.wagerCount === maxGames && candidate.wagerCount > suggested.wagerCount,
  );
  if (suggested.wagerCount < maxGames && longer && !laterWagersUsed) {
    const delta = marginalWDelta(suggested, longer);
    const extraSuccessRate =
      suggested.successfulEpisodes === 0 ? delta.deltaSuccesses : delta.deltaSuccesses / suggested.successfulEpisodes;
    if (extraSuccessRate < 0.08 && (delta.deltaProfitExposure ?? 0) <= 0) {
      reasons.push(
        'Extending beyond this range historically increased stopout exposure without producing enough additional successful recoveries',
      );
    }
  }
  return reasons;
}

function heatmapFrom(
  candidates: readonly EvaluatedCandidate[],
  region: CandidateRegion | null,
  suggested: EvaluatedCandidate | null,
): AutoAnalyzerReport['heatmap'] {
  const memberKeys = new Set((region?.members ?? []).map((member) => cellKey(member.entryDrought, member.wagerCount)));
  const suggestedKey = suggested ? cellKey(suggested.entryDrought, suggested.wagerCount) : null;
  const entries = candidates.map((candidate) => candidate.entryDrought);
  const wagers = candidates.map((candidate) => candidate.wagerCount);
  return {
    entryFrom: entries.length === 0 ? 0 : Math.min(...entries),
    entryTo: entries.length === 0 ? 0 : Math.max(...entries),
    wagerFrom: wagers.length === 0 ? 0 : Math.min(...wagers),
    wagerTo: wagers.length === 0 ? 0 : Math.max(...wagers),
    cells: candidates.map((candidate) => ({
      entryDrought: candidate.entryDrought,
      wagerCount: candidate.wagerCount,
      stopDrought: candidate.stopDrought,
      netPnl: candidate.netPnl,
      profitExposureRatio: candidate.profitExposureRatio,
      qualified: candidate.stage1 && candidate.stage2,
      inPrimaryRegion: memberKeys.has(cellKey(candidate.entryDrought, candidate.wagerCount)),
      isSuggested: suggestedKey === cellKey(candidate.entryDrought, candidate.wagerCount),
    })),
  };
}

function emptyReport(input: {
  targetMultiplier: number;
  maxGames: number;
  stakingMode: AnalyzerStakingMode;
  droughtSearch: AnalyzerSearchRange;
  consideredWagerCounts: number[];
  insufficientReason: string;
}): AutoAnalyzerReport {
  return {
    claimKind: 'simulated_historical_profitability',
    sampleLabel: 'IN-SAMPLE HISTORICAL SIMULATION',
    advisoryOnly: true,
    appliesToPaper: false,
    mutatesCollector: false,
    mutatesPaperConfig: false,
    verdict: 'INSUFFICIENT_HISTORICAL_EVIDENCE',
    insufficientReason: input.insufficientReason,
    targetMultiplier: input.targetMultiplier,
    maxGames: input.maxGames,
    stakingMode: input.stakingMode,
    baseStake: AUTO_ANALYZER_BASE_STAKE,
    minProfitExposure: AUTO_ANALYZER_MIN_PROFIT_EXPOSURE,
    droughtSearch: input.droughtSearch,
    consideredWagerCounts: input.consideredWagerCounts,
    candidateCount: 0,
    primary: null,
    secondary: [],
    heatmap: { entryFrom: 0, entryTo: 0, wagerFrom: 0, wagerTo: 0, cells: [] },
    details: {
      suggested: null,
      regionSize: null,
      walkForward: [],
      droughtSearch: input.droughtSearch,
      consideredEntries: input.droughtSearch.supportedEntries,
      consideredWagerCounts: input.consideredWagerCounts,
      qualifiedCount: 0,
      regionCount: 0,
      evaluatedMaxWager: input.maxGames,
      searchCells: 0,
    },
  };
}

function stripEpisodes(metrics: EvaluatedCandidate): CandidateMetrics {
  const { episodes: _episodes, ...rest } = metrics;
  return { ...rest, episodes: [] };
}

function neighborsOf(
  candidate: EvaluatedCandidate,
  index: Map<string, EvaluatedCandidate>,
): EvaluatedCandidate[] {
  const found: EvaluatedCandidate[] = [];
  for (let d = -1; d <= 1; d += 1) {
    for (let w = -1; w <= 1; w += 1) {
      if (d === 0 && w === 0) {
        continue;
      }
      const neighbor = index.get(cellKey(candidate.entryDrought + d, candidate.wagerCount + w));
      if (neighbor) {
        found.push(neighbor);
      }
    }
  }
  return found;
}

function compareRegions(left: CandidateRegion, right: CandidateRegion): number {
  const statusRank = (status: RegionStatus) => (status === 'STABLE' ? 0 : status === 'WEAKENING' ? 1 : 2);
  if (statusRank(left.status) !== statusRank(right.status)) {
    return statusRank(left.status) - statusRank(right.status);
  }
  if (left.members.length !== right.members.length) {
    return right.members.length - left.members.length;
  }
  if (left.walkForwardFoldsSupported !== right.walkForwardFoldsSupported) {
    return right.walkForwardFoldsSupported - left.walkForwardFoldsSupported;
  }
  const leftCoverage = median(left.members.map((member) => member.twoXCoverageBeforeNextStopoutRate ?? 0)) ?? 0;
  const rightCoverage = median(right.members.map((member) => member.twoXCoverageBeforeNextStopoutRate ?? 0)) ?? 0;
  if (leftCoverage !== rightCoverage) {
    return rightCoverage - leftCoverage;
  }
  return 0;
}

function compareCells(left: EvaluatedCandidate, right: EvaluatedCandidate): number {
  if (left.entryDrought !== right.entryDrought) {
    return left.entryDrought - right.entryDrought;
  }
  return left.wagerCount - right.wagerCount;
}

function classifyReasonFromStatus(status: RegionStatus): string {
  if (status === 'WEAKENING') {
    return 'The same profitable region is still present, but stopouts have consumed a larger share of profits in recent data.';
  }
  if (status === 'BROKEN') {
    return 'The previous profitable concentration no longer satisfies the required profit/exposure and historical stability checks.';
  }
  return 'The same profitable area persists and economics remain broadly healthy.';
}

function cellKey(entryDrought: number, wagerCount: number): string {
  return `${entryDrought}:${wagerCount}`;
}

function indexMetrics(metrics: readonly CandidateMetrics[]): Map<string, CandidateMetrics> {
  return new Map(metrics.map((item) => [cellKey(item.entryDrought, item.wagerCount), item]));
}

function axis(from: number, to: number): number[] {
  const values: number[] = [];
  for (let value = from; value <= to; value += 1) {
    values.push(value);
  }
  return values;
}

function rateAtLeast(values: readonly number[], threshold: number): number | null {
  if (values.length === 0) {
    return null;
  }
  return values.filter((value) => value >= threshold).length / values.length;
}

function chebyshev(entry: number, games: number, medianEntry: number, medianGames: number): number {
  return Math.max(Math.abs(entry - medianEntry), Math.abs(games - medianGames));
}

function majority(values: readonly number[]): number {
  if (values.length === 0) {
    return 0;
  }
  return Math.max(...values);
}
